/**
 * Context Menu Search History Hook for FlowRide Frontend
 * Handles adding context menu selections to search history
 */

import { useCallback } from "react";
import { appendSearchHistoryItem, searchHistorySlice, usePersistedSlice } from "../store/persistence";
import { SearchResultItem } from "../types/geocode-ui.types";

interface ContextMenuSearchHistoryOptions {

    /** Function to call when setting departure */
    "onSetDeparture"?: ( item: SearchResultItem ) => void;

    /** Function to call when setting arrival */
    "onSetArrival"?: ( item: SearchResultItem ) => void;
}

/**
 * Hook to handle context menu search history integration
 */
export const useContextMenuSearchHistory = ( {
    onSetDeparture,
    onSetArrival,
}: ContextMenuSearchHistoryOptions ) => {
    const [ , setSearchHistory ] = usePersistedSlice( searchHistorySlice );

    /**
     * Handle setting departure from context menu
     */
    const handleSetDeparture = useCallback( ( searchResultItem: SearchResultItem ) => {
        // Add to search history as departure
        setSearchHistory( ( previous ) => appendSearchHistoryItem( previous, searchResultItem ) );

        // Call the original onSetDeparture function
        onSetDeparture?.( searchResultItem );
    }, [ setSearchHistory, onSetDeparture ] );

    /**
     * Handle setting arrival from context menu
     */
    const handleSetArrival = useCallback( ( searchResultItem: SearchResultItem ) => {
        // Add to search history as arrival
        setSearchHistory( ( previous ) => appendSearchHistoryItem( previous, searchResultItem ) );

        // Call the original onSetArrival function
        onSetArrival?.( searchResultItem );
    }, [ setSearchHistory, onSetArrival ] );

    return {
        handleSetDeparture,
        handleSetArrival,
    };
};
